import { mapPriceAdjustmentUserOverride } from 'apiConnector/quote/trade';
import type { ApiErrorMessage } from 'epic/apiErrorEpicHelper';
import type { TradeRequestPayload } from 'epic/common-form/Trade';
import { ProductType } from 'state/quotes/details/details.model';
import type { QuoteInfos } from 'state/quotes/pendings/pendings.model';
import * as fromPending from 'state/quotes/pendings/pendings.selectors';
import * as fromQuoteResult from 'state/quotes/results/results.selectors';
import { getQuoteType, isLoadingPreviousQuote } from 'state/quotes/selectors';
import { getStreamingSequence } from 'state/quotes/streaming/streaming.selector';
import { getTradeNotificationsContacts } from 'state/quotes/trades/trades.selectors';
import { getSide } from 'state/selectors/common/helper';
import type { AppState } from 'state/state.model';
import * as fromStatic from 'state/static/infos/infos.selectors';
import { toExecutionBackToBack } from 'state/uiPanelState/tradeConfirmation/backToBack.utils';

import {
  getFormQuoteId,
  getSidePanelAddonAction,
  getSidePanelTradeWay,
  getStaticFormPriceEnhancementMode,
  getUserPreferencesLanguage,
  getUserPreferencesQuoteWorkspaceVersion,
  getUserPreferencesUnderlyingCodeType,
} from '..';
import {
  getQuoteExternalIdByWorkspaceTab,
  getTradeConfirmationByQuoteId,
  getTradeInfosByWorkspaceTab,
  getUserPriceEnhancement,
  getUserSponsoFees,
} from './temp';

export function getStaticFormInfos(state: AppState, tabId: string) {
  return fromStatic.getFormInfos(state.static, tabId);
}

export function getStaticTabIdsFromQuoteId(state: AppState, quoteId: string) {
  return fromStatic.getTabIdsByQuoteId(state.static, quoteId);
}

export function getStaticQuoteInfos(state: AppState, tabId: string): QuoteInfos | undefined {
  const quoteId = getStaticFormQuoteId(state, tabId);
  return quoteId
    ? fromQuoteResult.getQuoteInfos(state.quotes.results, quoteId)
    : fromPending.getQuoteInfos(state.quotes.pendings, tabId);
}

export function getStaticQuoteState(state: AppState, tabId: string) {
  const formInfos = getStaticFormInfos(state, tabId);
  return formInfos?.quoteState;
}

export function getStaticQuoteDetails(state: AppState, tabId: string) {
  const quoteId = getFormQuoteId(state, 'static', tabId);
  return quoteId ? state.quotes.details[quoteId] : undefined;
}

export function getStaticQuoteRequestStartingTimeInMillis(state: AppState, tabId: string) {
  const quoteInfos = getStaticQuoteInfos(state, tabId);
  return quoteInfos?.quoteRequestStartingTimeInMillis;
}

export function getStaticProductType(state: AppState, tabId: string): ProductType | undefined {
  const quoteDetails = getStaticQuoteDetails(state, tabId);
  return quoteDetails?.ProductType ?? undefined;
}
export function getStaticProductSubType(state: AppState, tabId: string): string | undefined {
  const quoteDetails = getStaticQuoteDetails(state, tabId);
  return quoteDetails?.ProductSubtype ?? undefined;
}
export function getStaticPricerRfqId(state: AppState, tabId: string): string | undefined {
  const quoteDetails = getStaticQuoteDetails(state, tabId);
  return quoteDetails?.PricerRfqId ?? undefined;
}

export function getStaticWrapper(state: AppState, tabId: string): string | undefined {
  const quoteDetails = getStaticQuoteDetails(state, tabId);
  return quoteDetails?.Wrapper ?? undefined;
}

export function getStaticInstrumentContractName(state: AppState, tabId: string): string | undefined {
  const quoteDetails = getStaticQuoteDetails(state, tabId);
  return quoteDetails?.InstrumentContractName ?? undefined;
}

export function getStaticTechnicalError(state: AppState, tabId: string): ApiErrorMessage | undefined {
  const quote = getStaticQuoteInfos(state, tabId);
  const trade = getTradeInfosByWorkspaceTab(state, 'static', tabId);
  return trade?.technicalError ?? quote?.technicalError;
}

export function getStaticFormValidationErrors(state: AppState, tabId: string) {
  return fromStatic.getFormValidationErrors(state.static, tabId);
}

export function getStaticFormQuoteId(state: AppState, tabId: string): string | undefined {
  return fromStatic.getFormQuoteId(state.static, tabId);
}

export function getStaticQuoteResult(state: AppState, tabId: string) {
  const quoteId = getStaticFormQuoteId(state, tabId);
  return quoteId ? fromQuoteResult.getQuoteResult(state.quotes.results, quoteId) : undefined;
}

export function getStaticPreviousQuoteIsLoading(state: AppState, tabId: string) {
  const quoteId = getStaticFormQuoteId(state, tabId);
  return isLoadingPreviousQuote(state, quoteId);
}

export function getTradeRequest(state: AppState, tabId: string) {
  const quote = getStaticQuoteResult(state, tabId)!;
  const quoteDetails = getStaticQuoteDetails(state, tabId);
  const { quoteId: internalQuoteId, instrumentName, productDescription } = getStaticFormInfos(state, tabId)!;
  const quoteType = getQuoteType(state, internalQuoteId);
  const externalQuoteId = getQuoteExternalIdByWorkspaceTab(state, 'static', tabId)!;
  const mode = getStaticFormPriceEnhancementMode(state, tabId);
  const priceEnhancement = getUserPriceEnhancement(state, 'static', tabId);
  const sponsoFees = getUserSponsoFees(state, 'static', tabId);
  const priceAdjustmentUserOverride = mapPriceAdjustmentUserOverride(mode, priceEnhancement);
  const tradeInfos = getTradeInfosByWorkspaceTab(state, 'static', tabId);
  const defaultUnderlyingCodeType = getUserPreferencesUnderlyingCodeType(state);
  const languageCode = getUserPreferencesLanguage(state);
  const quoteWorkspaceVersion = getUserPreferencesQuoteWorkspaceVersion(state);
  const wrapper = getStaticWrapper(state, tabId);
  const tradeWay = getSidePanelTradeWay(state, tabId);

  const executionOnly = quoteType === 'ExecOnly' || quoteType === 'AdvancedExecOnly';
  const side = getSide(quote.isExecOnlyAddon, tradeWay);
  const addonAction = getSidePanelAddonAction(state, tabId, 'static');
  const { priceEnhancement: defaultPriceEnhancement } = quote;
  const tradeNotifications = getTradeNotificationsContacts(state, internalQuoteId);
  const tradeRequest: TradeRequestPayload = {
    side,
    externalQuoteId: externalQuoteId!,
    internalQuoteId: internalQuoteId!,
    productSubType: quote.productSubType,
    instrumentContractName: quoteDetails?.InstrumentContractName ?? productDescription,
    instrumentName: quoteDetails?.InstrumentName ?? instrumentName,
    streamingSequence: getStreamingSequence(state, tabId),
    wrapper,
    screenMode: 'NewStatic',
    issuePrice: defaultPriceEnhancement?.issuePrice!,
    priceAdjustmentUserOverride,
    sponsoFees,
    executionOnly,
    comment: tradeInfos.comment,
    externalOrderId: tradeInfos.executionOnlyDealId,
    defaultUnderlyingCodeType,
    languageCode,
    quoteWorkspaceVersion,
    notifications: tradeNotifications,
    backToBack: toExecutionBackToBack(getTradeConfirmationByQuoteId(state, internalQuoteId)),
    addonAction,
  };

  return tradeRequest;
}
